import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDesignerScreenContext } from '../../components/Chat/DesignerScreenContext';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent, SyntheticEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ArrowBack, ArrowDropDown, Clear, Folder, Refresh, Save } from '@mui/icons-material';
import { Autocomplete, Box, Button, ButtonGroup, Chip, IconButton, InputAdornment, MenuItem, Popover, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

import { ContentClient } from '@@api';
import { useApiClient, useToastService, uuid } from '@@common';
import { ConfirmDialog, ViewTitleBar } from '@@components';

import { ApiException, ContentFolderClient, CreateStringTemplateRequest, EmailTemplateFields, ParameterMetadata, ParameterType, PrimitiveType, SchemaValue, TemplateFolderModel } from '../../api/api';
import { isChangeChatMessage } from '../../components/Chat/isChangeChatMessage';
import { ChatMessage } from '../../components/Chat/PromptChatAdapter';
import { useChangeSet } from '../../components/ChangeSetContext';
import SaveErrorDialog, { SaveError } from '../../components/SaveErrorDialog';
import FlowMetadataProvider from '../FlowDesigner/Components/Contexts/FlowMetadataContext';
import SchemaValueInput from '../FlowDesigner/Components/Inputs/SchemaValueInput';
import TemplateEditorContent from '../FlowDesigner/Components/Templates/TemplateEditorContent';
import { ExpressionModel } from '../FlowDesigner/Models/ExpressionModel';
import { ExpressionTypes } from '../FlowDesigner/Models/ExpressionTypes';
import { JSON_SCHEMA_TYPES } from '../FlowDesigner/Models/JsonSchemaModel';
import { SchemaValueModel } from '../FlowDesigner/Models/SchemaValueModel';
import {
	StringTemplateExpressionModel,
	StringTemplateFormat,
	TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE,
	TEMPLATE_CONTENT_PLACEHOLDER_ID,
	composeTemplateContent,
	extractPlaceholderIds,
	getPlaceholderIdAtPosition,
	normalizeStringTemplateFormat,
} from '../FlowDesigner/Models/StringTemplateModel';
import { VariableScope } from '../FlowDesigner/Models/VariableScopes/VariableScope';
import { getEntityMetadataModelFromJsonSchema } from '../FlowDesigner/Services/EntityMetadataModelConverter';
import TemplateChatPanel from './TemplateChatPanel';
import TemplatePlaceholderMenu from './TemplatePlaceholderMenu';
import { useTemplateChatAdapter } from './useTemplateChatAdapter';

type StringTemplateCategory = 'Email' | 'SMS';

interface TemplateFolderTreeNode extends TemplateFolderModel {
	children: TemplateFolderTreeNode[];
}

function renderFolderTreeNode(folder: TemplateFolderTreeNode) {
	return (
		<TreeItem
			key={folder.id}
			itemId={folder.id}
			label={
				<Stack direction="row" alignItems="center" spacing={1}>
					<Folder fontSize="small" />
					<Typography variant="body2">{folder.name}</Typography>
				</Stack>
			}
		>
			{folder.children.map((child) => renderFolderTreeNode(child))}
		</TreeItem>
	);
}

function getDefaultFormatForCategory(category: StringTemplateCategory): StringTemplateFormat {
	return category === 'SMS' ? 'Text' : 'Html';
}

const templateParameterMetadata: ParameterMetadata = {
	name: 'Content',
	displayName: 'Content',
	order: 0,
	categories: [],
	isCollection: false,
	type: ParameterType.Expression,
	expectedType: { type: PrimitiveType.String },
};

function buildVariableScope(inputSchemaValue: SchemaValueModel | undefined): VariableScope {
	const scope = new VariableScope();
	if (inputSchemaValue?.Value) {
		const entity = getEntityMetadataModelFromJsonSchema(inputSchemaValue.Value);
		scope.setVariable('Input', {
			name: 'Input',
			type: PrimitiveType.Object,
			isComplex: true,
			isMultiple: false,
			isRequired: false,
			referenceEntity: entity,
			referenceEntityId: entity.id,
		});
	}
	return scope;
}

function extendInputSchemaWithPlaceholders(inputSchemaValue: SchemaValueModel | undefined, ...contents: (string | undefined)[]): SchemaValueModel | undefined {
	const placeholderNames = contents.flatMap((content) => extractPlaceholderIds(content ?? '')).filter((name) => !name.startsWith('expr-') && name !== TEMPLATE_CONTENT_PLACEHOLDER_ID);
	if (placeholderNames.length === 0) {
		return inputSchemaValue;
	}

	const existingSchema = inputSchemaValue?.Value;
	const existingProperties = existingSchema?.properties ?? {};
	const missingNames = Array.from(new Set(placeholderNames)).filter((name) => !existingProperties[name]);
	if (missingNames.length === 0) {
		return inputSchemaValue;
	}

	return {
		Id: inputSchemaValue?.Id ?? uuid(),
		Name: inputSchemaValue?.Name || existingSchema?.title || 'Input',
		Value: {
			...existingSchema,
			type: JSON_SCHEMA_TYPES.OBJECT,
			title: existingSchema?.title || inputSchemaValue?.Name || 'Input',
			properties: {
				...existingProperties,
				...Object.fromEntries(missingNames.map((name) => [name, { type: JSON_SCHEMA_TYPES.STRING }])),
			},
			required: existingSchema?.required ?? [],
		},
	};
}

function mapSchemaValueFromApi(schemaValue: SchemaValue | undefined): SchemaValueModel | undefined {
	if (!schemaValue) {
		return undefined;
	}

	return {
		Id: schemaValue.id,
		Name: schemaValue.name,
		Value: schemaValue.value,
	};
}

function mapSchemaValueToApi(schemaValue: SchemaValueModel | undefined): SchemaValue | undefined {
	if (!schemaValue) {
		return undefined;
	}

	return {
		id: schemaValue.Id,
		name: schemaValue.Name,
		value: schemaValue.Value,
	};
}

function getInputPlaceholderNames(schemaValue: SchemaValueModel | undefined): string[] {
	return Object.entries(schemaValue?.Value?.properties ?? {})
		.filter(([name, schema]) => /^[A-Za-z_][A-Za-z0-9_.-]*$/.test(name) && schema.type !== JSON_SCHEMA_TYPES.OBJECT && schema.type !== JSON_SCHEMA_TYPES.ARRAY)
		.map(([name]) => name);
}

function convertTemplateContent(content: string, currentFormat: StringTemplateFormat, nextFormat: StringTemplateFormat): string {
	if (nextFormat === currentFormat) {
		return content;
	}

	if (nextFormat !== 'Html' && currentFormat === 'Html') {
		const doc = new DOMParser().parseFromString(content, 'text/html');
		doc.querySelectorAll('p').forEach((p) => {
			const frag = doc.createDocumentFragment();
			frag.append(...Array.from(p.childNodes));
			frag.appendChild(doc.createTextNode('\n'));
			p.replaceWith(frag);
		});
		doc.querySelectorAll('br').forEach((br) => br.replaceWith(doc.createTextNode('\n')));
		return (doc.body.textContent ?? '').replace(/\n$/, '');
	}

	if (nextFormat === 'Html' && currentFormat !== 'Html') {
		return content
			.split('\n')
			.map((line) => `<p>${line}</p>`)
			.join('');
	}

	return content;
}

function ContentTemplateEditorContent() {
	const { id = 'new' } = useParams();
	const isNew = id === 'new';
	const navigate = useNavigate();
	const location = useLocation();
	const client = useApiClient(ContentClient);
	const folderClient = useApiClient(ContentFolderClient);
	const queryClient = useQueryClient();
	const toastService = useToastService();
	const { currentChangeSetId } = useChangeSet();

	const [name, setName] = useState('');
	useDesignerScreenContext({ ScreenArea: 'Content', ScreenName: name, CurrentTemplateId: isNew ? undefined : id });
	const [category, setCategory] = useState<StringTemplateCategory>('Email');
	const [parentTemplateId, setParentTemplateId] = useState<string | undefined>();
	const [folderId, setFolderId] = useState<string | undefined>(() => new URLSearchParams(location.search).get('folderId') || undefined);
	const [folderPickerAnchor, setFolderPickerAnchor] = useState<HTMLElement | null>(null);
	const [folderSearch, setFolderSearch] = useState('');
	const [expandedFolderIds, setExpandedFolderIds] = useState<string[]>([]);
	const [pendingCategory, setPendingCategory] = useState<StringTemplateCategory | undefined>();
	const [inputSchemaValue, setInputSchemaValue] = useState<SchemaValueModel | undefined>();
	const [emailFields, setEmailFields] = useState<EmailTemplateFields>({});
	const [emailBodyTab, setEmailBodyTab] = useState(0);
	const [activePlaceholderId, setActivePlaceholderId] = useState<string>();
	const [expression, setExpression] = useState<StringTemplateExpressionModel>({
		Type: ExpressionTypes.StringTemplate,
		Format: 'Html',
		Content: '',
		Placeholders: {},
	});
	const isChangeRefreshHandledRef = useRef(false);

	const { data: template, isLoading, refetch: refetchTemplate } = useQuery({
		queryKey: ['content', 'template', id],
		enabled: !isNew,
		queryFn: () => client.getById(id),
	});

	useEffect(() => {
		if (!template) {
			return;
		}

		setName(template.name);
		setCategory(template.category as StringTemplateCategory);
		setParentTemplateId(template.parentTemplateId);
		setFolderId(template.folderId);
		setInputSchemaValue(mapSchemaValueFromApi(template.inputSchemaValue));
		setEmailFields({
			subject: template.email?.subject ?? '',
			to: template.email?.to ?? '',
			cc: template.email?.cc ?? '',
			bcc: template.email?.bcc ?? '',
			plainTextBody: template.email?.plainTextBody ?? '',
		});
		setExpression({
			Type: ExpressionTypes.StringTemplate,
			Format: template.category === 'Email' ? 'Html' : normalizeStringTemplateFormat(template.format),
			Content: template.category === 'Email' && normalizeStringTemplateFormat(template.format) !== 'Html'
				? convertTemplateContent(template.content ?? '', normalizeStringTemplateFormat(template.format), 'Html')
				: template.content ?? '',
			Placeholders: (template.properties ?? {}) as Record<string, ExpressionModel>,
		});
	}, [template]);

	const { data: masterTemplates } = useQuery({
		queryKey: ['content', 'templates', 'masters', currentChangeSetId, category],
		queryFn: () => client.search(0, 200, 'Name', true, currentChangeSetId ?? undefined, category, undefined, undefined),
	});
	const { data: folders } = useQuery({ queryKey: ['content', 'folders'], queryFn: () => folderClient.search(undefined) });
	const folderTree = useMemo(() => {
		const nodes = new Map<string, TemplateFolderTreeNode>();
		for (const folder of folders ?? []) {
			nodes.set(folder.id, { ...folder, children: [] });
		}

		const roots: TemplateFolderTreeNode[] = [];
		for (const folder of folders ?? []) {
			const node = nodes.get(folder.id)!;
			const parent = folder.parentId ? nodes.get(folder.parentId) : undefined;
			if (parent) parent.children.push(node);
			else roots.push(node);
		}

		const search = folderSearch.trim().toLocaleLowerCase();
		if (!search) return { nodes: roots, matchingExpandedIds: [] as string[] };

		const matchingExpandedIds: string[] = [];
		const filterNodes = (items: TemplateFolderTreeNode[]): TemplateFolderTreeNode[] => items.flatMap((node) => {
			const children = filterNodes(node.children);
			if (!node.name.toLocaleLowerCase().includes(search) && children.length === 0) return [];
			if (children.length > 0) matchingExpandedIds.push(node.id);
			return [{ ...node, children }];
		});

		return { nodes: filterNodes(roots), matchingExpandedIds };
	}, [folders, folderSearch]);

	const selectedParentTemplate = useMemo(
		() => masterTemplates?.items.find((template) => template.id === parentTemplateId),
		[masterTemplates, parentTemplateId],
	);

	const previewContent = useMemo(
		() => composeTemplateContent(selectedParentTemplate?.content, expression.Content),
		[selectedParentTemplate, expression.Content],
	);
	const editorRevisionKey = useMemo(
		() => [template?.id, template?.versionId, template?.updatedDate, template?.content].filter(Boolean).join(':') || 'new',
		[template?.id, template?.versionId, template?.updatedDate, template?.content],
	);

	const variableScope = useMemo(() => buildVariableScope(inputSchemaValue), [inputSchemaValue]);
	const inputPlaceholderNames = useMemo(() => getInputPlaceholderNames(inputSchemaValue), [inputSchemaValue]);
	const [saveError, setSaveError] = useState<SaveError>();

	const applyCategoryChange = useCallback((nextCategory: StringTemplateCategory) => {
		const nextFormat = getDefaultFormatForCategory(nextCategory);
		setCategory(nextCategory);
		setParentTemplateId(undefined);
		setExpression((current) => ({
			...current,
			Format: nextFormat,
			Content: convertTemplateContent(current.Content, current.Format, nextFormat),
		}));
	}, []);

	const handleCategoryChange = useCallback(
		(nextCategory: StringTemplateCategory) => {
			if (nextCategory === category) {
				return;
			}

			const nextFormat = getDefaultFormatForCategory(nextCategory);
			if (nextFormat !== expression.Format) {
				setPendingCategory(nextCategory);
				return;
			}

			setCategory(nextCategory);
			setParentTemplateId(undefined);
		},
		[category, expression.Format],
	);

	const handleExpressionChange = useCallback(
		(next: ExpressionModel) => {
			const nextExpression = next as StringTemplateExpressionModel;
			const nextInputSchemaValue = extendInputSchemaWithPlaceholders(inputSchemaValue, nextExpression.Content, ...(category === 'Email' ? Object.values(emailFields) : []));
			setExpression(nextExpression);
			if (nextInputSchemaValue !== inputSchemaValue) {
				setInputSchemaValue(nextInputSchemaValue);
				toastService.success('Template placeholders added to the input schema.');
			}
		},
		[inputSchemaValue, emailFields, category, toastService],
	);

	const updateEmailField = useCallback((key: keyof EmailTemplateFields, value: string) => {
		setEmailFields((current) => ({ ...current, [key]: value }));
		setInputSchemaValue((current) => extendInputSchemaWithPlaceholders(current, value));
	}, []);

	const insertIntoEmailField = useCallback((key: keyof EmailTemplateFields, element: HTMLInputElement | HTMLTextAreaElement, name: string) => {
		const token = `{{${name}}}`;
		const start = element.selectionStart ?? element.value.length;
		const end = element.selectionEnd ?? start;
		const nextValue = `${element.value.slice(0, start)}${token}${element.value.slice(end)}`;
		setEmailFields((current) => ({ ...current, [key]: nextValue }));
		setInputSchemaValue((current) => extendInputSchemaWithPlaceholders(current, nextValue));
		requestAnimationFrame(() => {
			element.focus();
			const caret = start + token.length;
			element.setSelectionRange(caret, caret);
		});
	}, []);

	const handleEmailFieldDrop = useCallback((key: keyof EmailTemplateFields, event: DragEvent<HTMLDivElement>) => {
		const name = event.dataTransfer.getData(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE);
		if (!name) {
			return;
		}

		event.preventDefault();
		const element = event.currentTarget.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null;
		if (element) {
			insertIntoEmailField(key, element, name);
		}
	}, [insertIntoEmailField]);

	const handleEmailFieldSelection = useCallback((event: SyntheticEvent<HTMLDivElement>) => {
		const target = event.target;
		const element = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
			? target
			: event.currentTarget.querySelector('input, textarea');
		if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
			setActivePlaceholderId(getPlaceholderIdAtPosition(element.value, element.selectionStart ?? element.value.length));
		}
	}, []);

	const insertIntoPlainTextBody = useCallback((name: string) => {
		const element = document.querySelector<HTMLTextAreaElement>('[data-plain-text-body] textarea');
		if (element) {
			insertIntoEmailField('plainTextBody', element, name);
		}
	}, [insertIntoEmailField]);

	const saveMutation = useMutation({
		onMutate: () => setSaveError(undefined),
		mutationFn: async () => {
			const request: CreateStringTemplateRequest = {
				changeSetId: currentChangeSetId ?? '',
				name,
				parentTemplateId,
				folderId,
				category,
				content: expression.Content,
				format: expression.Format,
				properties: expression.Placeholders as CreateStringTemplateRequest['properties'],
				inputSchemaValue: mapSchemaValueToApi(extendInputSchemaWithPlaceholders(inputSchemaValue, expression.Content, ...(category === 'Email' ? Object.values(emailFields) : []))),
				email: category === 'Email' ? emailFields : undefined,
			};

			if (isNew) {
				return client.create(request);
			}

			return client.update(id, { ...request, setFolderId: true });
		},
		onSuccess: async (templateId) => {
			toastService.success('Template saved.');
			await queryClient.invalidateQueries({ queryKey: ['content'] });
			navigate({ pathname: `/template/${templateId}`, search: location.search });
		},
		onError: (error) => {
			let saveError: SaveError = {
				title: 'Failed to save template',
				detail: error instanceof Error && error.message ? error.message : 'An unexpected error occurred while saving the template.',
				validationIssues: [],
			};

			if (ApiException.isApiException(error)) {
				try {
					const body = JSON.parse(error.response) as { detail?: string; title?: string; traceId?: string; TraceId?: string };
					saveError = {
						title: body.title ?? saveError.title,
						detail: body.detail ?? body.title ?? saveError.detail,
						traceId: body.traceId ?? body.TraceId,
						validationIssues: [],
					};
				} catch {
					// Response wasn't JSON; use the generated client's error message.
				}
			}

			setSaveError(saveError);
		},
	});

	const canSave = !!currentChangeSetId && name.trim().length > 0 && !saveMutation.isPending && !isLoading;
	const chatContext = useMemo(
		() => ({
			changeSetId: currentChangeSetId,
			name,
			category,
			inputSchemaValue,
		}),
		[currentChangeSetId, name, category, inputSchemaValue],
	);
	const chatAdapter = useTemplateChatAdapter(isNew ? undefined : id, chatContext);
	const refreshTemplate = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: ['content'] });
		if (!isNew) {
			await refetchTemplate();
		}
	}, [isNew, queryClient, refetchTemplate]);
	const handleAssistantMessageReceived = useCallback(async (message: ChatMessage) => {
		if (!isChangeChatMessage(message)) {
			return;
		}

		isChangeRefreshHandledRef.current = true;
		await refreshTemplate();
	}, [refreshTemplate]);
	const handleChatComplete = useCallback(async () => {
		if (isChangeRefreshHandledRef.current) {
			isChangeRefreshHandledRef.current = false;
			return;
		}

		await refreshTemplate();
	}, [refreshTemplate]);

	return (
		<Stack direction="column" sx={{ flexGrow: 1, minHeight: 0 }}>
			<SaveErrorDialog error={saveError} onClose={() => setSaveError(undefined)} />
			<ConfirmDialog
				open={pendingCategory !== undefined}
				title="Change Template Format"
				message="Changing the category will change the template format and can erase formatting. Do you want to continue?"
				confirmText="Change Format"
				confirmColor="warning"
				onConfirm={() => {
					if (pendingCategory) {
						applyCategoryChange(pendingCategory);
					}
					setPendingCategory(undefined);
				}}
				onCancel={() => setPendingCategory(undefined)}
			/>

			<ViewTitleBar title={isNew ? 'New Template' : name || 'Template'}>
				<ButtonGroup variant="outlined">
					<Button startIcon={<ArrowBack />} onClick={() => navigate({ pathname: '/template', search: location.search })}>
						Back
					</Button>
					<Button startIcon={<Refresh />} disabled={isNew || isLoading} onClick={() => void refreshTemplate()}>
						Refresh
					</Button>
					<Button variant="contained" startIcon={<Save />} disabled={!canSave} onClick={() => saveMutation.mutate()}>
						Save
					</Button>
				</ButtonGroup>
			</ViewTitleBar>

			<Stack direction="row" sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
				<Stack direction="column" sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(12, minmax(0, 1fr))' },
							gap: 1.5,
							alignItems: 'center',
							px: 2,
							py: 1.5,
							borderBottom: 1,
							borderColor: 'divider',
						}}
					>
						<TextField
							size="small"
							label="Name"
							value={name}
							onChange={(event) => setName(event.target.value)}
							fullWidth
							sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', sm: '1 / -1', lg: 'span 6' } }}
						/>
						<TextField
							select
							size="small"
							label="Category"
							value={category}
							onChange={(event) => handleCategoryChange(event.target.value as StringTemplateCategory)}
							fullWidth
							sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', sm: 'span 1', lg: 'span 2' } }}
						>
							<MenuItem value="Email">Email</MenuItem>
							<MenuItem value="SMS">SMS</MenuItem>
						</TextField>
						<TextField
							size="small"
							label="Folder"
							value={folders?.find((folder) => folder.id === folderId)?.name ?? ''}
							placeholder="Choose a folder"
							fullWidth
							inputProps={{ readOnly: true }}
							onClick={(event) => setFolderPickerAnchor(event.currentTarget)}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										{folderId && (
											<IconButton
												size="small"
												aria-label="Clear folder"
												onClick={(event) => {
													event.stopPropagation();
													setFolderId(undefined);
												}}
											>
												<Clear fontSize="small" />
											</IconButton>
										)}
										<ArrowDropDown />
									</InputAdornment>
								),
							}}
							sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', sm: 'span 1', lg: 'span 4' } }}
						/>
						<Popover
							open={Boolean(folderPickerAnchor)}
							anchorEl={folderPickerAnchor}
							onClose={() => {
								setFolderPickerAnchor(null);
								setFolderSearch('');
							}}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
							transformOrigin={{ vertical: 'top', horizontal: 'left' }}
							PaperProps={{ sx: { width: Math.max(folderPickerAnchor?.clientWidth ?? 280, 280), maxHeight: 420, p: 1 } }}
						>
							<Stack spacing={1}>
								<TextField
									autoFocus
									size="small"
									placeholder="Search folders"
									value={folderSearch}
									onChange={(event) => setFolderSearch(event.target.value)}
								/>
								<SimpleTreeView
									selectedItems={folderId ?? null}
									expandedItems={folderSearch.trim() ? folderTree.matchingExpandedIds : expandedFolderIds}
									onExpandedItemsChange={(_, itemIds) => setExpandedFolderIds(itemIds)}
									onSelectedItemsChange={(_, itemId) => {
										if (itemId) {
											setFolderId(itemId);
											setFolderPickerAnchor(null);
											setFolderSearch('');
										}
									}}
								>
									{folderTree.nodes.map((folder) => renderFolderTreeNode(folder))}
								</SimpleTreeView>
								{folderTree.nodes.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>No folders found</Typography>}
							</Stack>
						</Popover>
						<Autocomplete
							options={masterTemplates?.items.filter((template) => template.id !== id) ?? []}
							value={masterTemplates?.items.find((template) => template.id === parentTemplateId) ?? null}
							getOptionLabel={(template) => template.name ?? ''}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							onChange={(_, template) => setParentTemplateId(template?.id)}
							size="small"
							fullWidth
							sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', sm: '1 / -1', lg: 'span 4' } }}
							renderInput={(params) => <TextField {...params} label="Master Template" placeholder="Search templates" />}
						/>
						<Stack spacing={0.5} sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', sm: '1 / -1', lg: 'span 8' } }}>
							<SchemaValueInput schemaValue={inputSchemaValue} onSchemaValueChange={(value) => {
								if (!value || 'Value' in value) setInputSchemaValue(value);
							}} />
							<Typography variant="caption" color="text.secondary">
								Input schema defines available placeholders.
							</Typography>
						</Stack>
					</Box>
					{category === 'Email' && inputPlaceholderNames.length > 0 && (
						<Box sx={{ px: 2, pt: 1, pb: 0.5, borderBottom: 1, borderColor: 'divider' }}>
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
								Input placeholders
							</Typography>
							<Stack direction="row" flexWrap="wrap" gap={0.5}>
								{inputPlaceholderNames.map((placeholderName) => (
									<Chip
										key={placeholderName}
										label={placeholderName}
										size="small"
										variant={activePlaceholderId === placeholderName ? 'filled' : 'outlined'}
										color="primary"
										title={`Drag to insert {{${placeholderName}}}`}
										draggable
										onDragStart={(event) => {
											event.dataTransfer.setData(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE, placeholderName);
											event.dataTransfer.effectAllowed = 'copy';
										}}
										sx={{ cursor: 'grab' }}
									/>
								))}
							</Stack>
						</Box>
					)}

					{category === 'Email' && (
						<Stack spacing={1.5} sx={{ px: 2, pt: 2 }}>
							{(['subject', 'to'] as const).map((key) => (
								<TextField
									key={key}
									size="small"
									label={{ subject: 'Subject', to: 'To', cc: 'Cc', bcc: 'Bcc' }[key]}
									fullWidth
									value={emailFields[key] ?? ''}
									onChange={(event) => updateEmailField(key, event.target.value)}
									onSelect={handleEmailFieldSelection}
									onKeyUp={handleEmailFieldSelection}
									onClick={handleEmailFieldSelection}
									onDragOver={(event) => {
										if (event.dataTransfer.types.includes(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE)) event.preventDefault();
									}}
									onDrop={(event) => handleEmailFieldDrop(key, event)}
								/>
							))}
							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
								{(['cc', 'bcc'] as const).map((key) => (
									<TextField
										key={key}
										size="small"
										label={key.toUpperCase()}
										fullWidth
										value={emailFields[key] ?? ''}
										onChange={(event) => updateEmailField(key, event.target.value)}
										onSelect={handleEmailFieldSelection}
										onKeyUp={handleEmailFieldSelection}
										onClick={handleEmailFieldSelection}
										onDragOver={(event) => {
											if (event.dataTransfer.types.includes(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE)) event.preventDefault();
										}}
										onDrop={(event) => handleEmailFieldDrop(key, event)}
									/>
								))}
							</Stack>
							<Tabs value={emailBodyTab} onChange={(_, value: number) => setEmailBodyTab(value)} variant="fullWidth">
								<Tab label="HTML body" />
								<Tab label="Plain Text Body" />
							</Tabs>
						</Stack>
					)}
					<Box sx={{ flex: 1, minHeight: 0, p: 2, display: category === 'Email' && emailBodyTab === 1 ? 'none' : 'block' }}>
						<TemplateEditorContent
							key={editorRevisionKey}
							expression={expression as ExpressionModel}
							onExpressionChange={handleExpressionChange}
							format={expression.Format}
							parameterMetadata={templateParameterMetadata}
							variableScope={variableScope}
							previewContent={previewContent}
							showContentSlotButton
										availablePlaceholderIds={inputPlaceholderNames}
										showFormatSelector={false}
										showNewPlaceholderButton={false}
										showPlaceholderLegend={false}
										onActivePlaceholderChange={setActivePlaceholderId}
						/>
					</Box>
					{category === 'Email' && emailBodyTab === 1 && (
						<Box sx={{ display: 'flex', flex: 1, minHeight: 0, p: 2 }}>
							<Stack direction="column" sx={{ flex: 1, minHeight: 0, width: '100%' }}>
								<Box sx={{ flexShrink: 0, mb: 0.5 }}>
									<TemplatePlaceholderMenu placeholderNames={inputPlaceholderNames} onSelect={insertIntoPlainTextBody} />
								</Box>
								<TextField
									data-plain-text-body
									label="Plain-text alternative"
									multiline
									fullWidth
									value={emailFields.plainTextBody ?? ''}
									onChange={(event) => updateEmailField('plainTextBody', event.target.value)}
									onSelect={handleEmailFieldSelection}
									onKeyUp={handleEmailFieldSelection}
									onClick={handleEmailFieldSelection}
									onDragOver={(event) => {
										if (event.dataTransfer.types.includes(TEMPLATE_PLACEHOLDER_DRAG_DATA_TYPE)) event.preventDefault();
									}}
									onDrop={(event) => handleEmailFieldDrop('plainTextBody', event)}
									sx={{
										flex: 1,
										minHeight: 0,
										'& .MuiInputBase-root': { height: '100%', alignItems: 'stretch' },
										'& .MuiInputBase-inputMultiline': { height: '100% !important', overflowY: 'auto !important' },
									}}
								/>
							</Stack>
						</Box>
					)}
				</Stack>
				<TemplateChatPanel adapter={chatAdapter} onComplete={handleChatComplete} onAssistantMessageReceived={handleAssistantMessageReceived} />
			</Stack>
		</Stack>
	);
}

export default function ContentTemplateEditorView() {
	return (
		<FlowMetadataProvider>
			<ContentTemplateEditorContent />
		</FlowMetadataProvider>
	);
}
